import { computed, reactive } from 'vue'
import axios from 'axios'

const state = reactive({
  user: {},
  isAuthenticated: false,
  isEmulating: false,
  isAdministrator: false,
})

export default function useAuth() {
  const authenticated = computed(() => state.isAuthenticated)
  const user = computed(() => state.user)
  const emulating = computed(() => state.isEmulating)
  const administrator = computed(() => state.isAdministrator)

  const setAuthenticated = (value: boolean) => {
    state.isAuthenticated = value
  }

  const setEmulation = (value: boolean) => {
    state.isEmulating = value
  }

  const setUser = (value: object) => {
    state.user = value
  }

  const setAdministrator = (value: boolean) => {
    state.isAdministrator = value
  }

  const getUser = async (username: string, includeGroups = false, includeRetailers = false) => {
    try {
      const params = new URLSearchParams()
      if (username) {
        params.append('username', username)
        params.append('emulating', 'true')
      }
      params.append('include_groups', includeGroups.toString())
      params.append('include_retailers', includeRetailers.toString())

      const response = await axios.get(`/api/user?${params.toString()}`)

      const user = response.data
      if (user.retailers && user.groups) {
        user.retailers = user.retailers.concat(
          user.groups.flatMap((group: { retailers?: unknown[] }) => group.retailers || []),
        )
        user.retailers = user.retailers.filter(
          (retailer: { id: unknown }, index: number, self: Array<{ id: unknown }>) =>
            index === self.findIndex((t: { id: unknown }) => t.id === retailer.id),
        )
      }

      setAuthenticated(true)
      setEmulation(username ? true : false)
      setUser(user)

      return user
    } catch {
      setAuthenticated(false)
      setEmulation(false)
      setAdministrator(false)
      setUser({})
      return null
    }
  }

  const getUserGroups = async (username: string | null = null, search: string = '') => {
    try {
      const params: Record<string, string> = {}
      if (username) params.username = username
      if (search) params.search = search

      const response = await axios.get('/api/user/groups', { params })
      const groups = response.data || []

      return groups.sort((a: { label: string }, b: { label: string }) => {
        const labelA = (a.label || '').toLowerCase()
        const labelB = (b.label || '').toLowerCase()
        return labelA.localeCompare(labelB)
      })
    } catch (err) {
      console.error('Error loading groups:', err)
      return []
    }
  }

  const getUserRetailers = async (
    username: string | null = null,
    search: string = '',
    region: string | null = null,
    zone: string | null = null,
  ) => {
    try {
      const params: Record<string, string> = {}
      if (username) params.username = username
      if (search) params.search = search
      if (region) params.region = region
      if (zone) params.zone = zone

      const response = await axios.get('/api/user/retailers', { params })
      const retailers = response.data || []

      return retailers.sort((a: { label: string }, b: { label: string }) => {
        const labelA = (a.label || '').toLowerCase()
        const labelB = (b.label || '').toLowerCase()
        return labelA.localeCompare(labelB)
      })
    } catch (err) {
      console.error('Error loading retailers:', err)
      return []
    }
  }

  const getUsers = async (username: string | null = null) => {
    try {
      const response = await axios.get('/api/users' + (username ? `?username=${username}` : ''))
      return response.data
    } catch {
      return []
    }
  }

  const logout = async () => {
    try {
      await axios.post('/api/logout')
      setAuthenticated(false)
      setEmulation(false)
      setAdministrator(false)
      setUser({})
      return true
    } catch {
      setAuthenticated(false)
      setEmulation(false)
      setAdministrator(false)
      setUser({})
      return false
    }
  }

  return {
    authenticated,
    user,
    state,
    emulating,
    administrator,
    getUser,
    getUsers,
    getUserGroups,
    getUserRetailers,
    setAuthenticated,
    setUser,
    logout,
  }
}
